import { useMemo } from "react";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import i18n from "i18n";

/**
 * Information card : Title / Description / Image, click handler
 * @param {object} props - Component props
 * @param {Function} props.onClick - Callback function when card is clicked (disabled when disabled=true)
 * @param {boolean} props.selected - If true, highlights the card with blue background
 * @param {object} props.element - Card content configuration
 * @param {string} props.element.title - Card title (i18n key)
 * @param {string} props.element.description - Card description (i18n key)
 * @param {string} [props.element.image] - Optional image URL for the card
 * @param {boolean} [props.disabled=false] - If true, disables the card interaction and shows visual feedback (reduced opacity, gray background, not-allowed cursor)
 * @returns {React.ReactElement} Styled card component with conditional styling based on selection and disabled state
 * @example
 * <InformationCard
 *   element={{
 *     title: "chart.types.bar",
 *     description: "chart.types.bar.description",
 *     image: "./chart-preview.png"
 *   }}
 *   onClick={() => handleSelectChart("bar")}
 *   selected={chartType === "bar"}
 *   disabled={itemCount === 0}
 * />
 */
const InformationCard = ({ onClick, selected, element, disabled = false }) => {
	const { cardBackgroundColor, hoverBackgroundColor } = useMemo(() => {
		let cardBg = "";
		if (selected) {
			cardBg = "#1A73E8";
		} else if (disabled) {
			cardBg = "#f5f5f5";
		}

		let hoverBg = "#eeeeee";
		if (disabled) {
			hoverBg = "#f5f5f5";
			if (selected) {
				hoverBg = "#1A73E8";
			}
		}

		return { cardBackgroundColor: cardBg, hoverBackgroundColor: hoverBg };
	}, [selected, disabled]);

	return (
		<MDBox
			m={1}
			mt={4}
			className="cardGraphChoice"
			onClick={disabled ? undefined : onClick}
			p={3}
			borderRadius="xl"
			aria-disabled={disabled}
			style={{
				backgroundColor: cardBackgroundColor
			}}
			sx={{
				transition: "background-color 0.1s ease-in-out, opacity 0.1s ease-in-out",
				cursor: disabled ? "not-allowed" : "pointer",
				opacity: disabled ? 0.6 : 1,
				"&:hover": {
					backgroundColor: hoverBackgroundColor
				}
			}}
		>
			<MDBox display="flex">
				<MDBox flex="1">
					<MDTypography variant="h3" color={selected ? "white" : "dark"}>
						{i18n.t(element.title)}
					</MDTypography>
					<MDTypography variant="body2" color={selected ? "white" : "dark"}>
						{i18n.t(element.description)}
					</MDTypography>
				</MDBox>
				{element.image && (
					<MDBox
						flex="3"
						position="relative"
						borderRadius="lg"
						mx={2}
						display="flex"
						justifyContent="center"
						alignItems="center"
					>
						<MDBox
							component="img"
							src={element.image}
							borderRadius="lg"
							shadow="md"
							width="auto"
							height="100%"
							position="relative"
							zIndex={1}
							maxHeight="30vh"
						/>
						<MDBox
							borderRadius="lg"
							shadow="md"
							width="100%"
							height="100%"
							position="absolute"
							left={0}
							top="3%"
							sx={{
								backgroundImage: `url(${element.image})`,
								transform: "scale(0.94)",
								filter: "blur(12px)",
								backgroundSize: "cover"
							}}
						/>
					</MDBox>
				)}
			</MDBox>
		</MDBox>
	);
};

export default InformationCard;
